"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useAuth } from "./auth-context";

function AccountIcon({ avatarUrl }: { avatarUrl: string | null }) {
  if (avatarUrl) {
    // width/height attributes (not just the CSS class) are load-bearing here: an <img> inside a flex
    // container is itself a flex item, and flex items default to min-width/min-height:auto -- which
    // for a replaced element like <img> means "never shrink below natural size," silently defeating
    // width:100%/height:100% in the stylesheet. Google's photo URL returns ~96px; without explicit
    // pixel attributes it rendered at that size instead of the intended 26px.
    // eslint-disable-next-line @next/next/no-img-element -- external provider-hosted URL, not a local asset next/image can optimize
    return <img src={avatarUrl} alt="" className="auth-avatar" width={26} height={26} referrerPolicy="no-referrer" />;
  }
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function AuthMenu() {
  const { loggedIn, loading, avatarUrl, isAdmin, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);

  if (loading) return null;

  return (
    <div className="auth-menu" ref={menuRef}>
      <button
        type="button"
        className="auth-icon-btn"
        aria-label="Account"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <AccountIcon avatarUrl={loggedIn ? avatarUrl : null} />
      </button>

      {isOpen && (
        <div className="auth-dropdown">
          {loggedIn ? (
            <>
              <Link href="/account/orders" className="auth-dropdown-item" onClick={() => setIsOpen(false)}>
                My Orders
              </Link>
              <Link href="/account/addresses" className="auth-dropdown-item" onClick={() => setIsOpen(false)}>
                My Addresses
              </Link>
              {isAdmin && (
                <Link href="/admin" className="auth-dropdown-item" onClick={() => setIsOpen(false)}>
                  Admin
                </Link>
              )}
              <button
                type="button"
                className="auth-dropdown-item"
                onClick={() => {
                  setIsOpen(false);
                  logout();
                }}
              >
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link href="/login" className="auth-dropdown-item" onClick={() => setIsOpen(false)}>
                Log In
              </Link>
              <Link href="/signup" className="auth-dropdown-item" onClick={() => setIsOpen(false)}>
                Sign Up
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
